/* ==========================================================================
   Macaje V4 — theme.js
   Vanilla custom elements, no dependencies. Each behaviour is a self-registering
   element so it binds on load AND on Theme Editor section re-render, with no
   central init to keep in sync.
   ========================================================================== */

(() => {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  const on = (el, ev, fn, opts) => el.addEventListener(ev, fn, opts);

  // Shopify.formatMoney isn't guaranteed on the storefront — render through the
  // shop's money_format template ourselves.
  const money = (cents) => {
    const fmt = window.themeStrings?.moneyFormat || '${{amount}}';
    const n = cents / 100;
    const amount = /amount_no_decimals/.test(fmt) ? String(Math.round(n)) : n.toFixed(2);
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/g, amount).replace(/<[^>]*>/g, '');
  };

  // Store-wide price format (option B — "Playfair, elegant"): the plain amount in
  // the shop's money format. Numbers render in Playfair serif via the 'Macaje
  // Num' @font-face. Single source so every JS-rendered price matches the Liquid
  // `money` snippet exactly.
  const moneyHTML = (cents) => money(cents);

  // Every visible cart-count badge (header bag icon, mobile menu, etc.) must
  // show the same number as the products actually listed in the cart drawer —
  // never Shopify's raw cart.item_count, which also counts the Luxe Club
  // Membership/Application Fee lines that cart-items.liquid and cart-drawer.liquid
  // deliberately hide from the visible list (see cart-progress.liquid's third-pass
  // fix for the same class of bug). window.themeStrings.excludedProductIds is
  // populated server-side from the same two theme settings, so this can never
  // drift out of sync with what Liquid excludes.
  const qualifyingCartCount = (cart) => {
    const excluded = window.themeStrings?.excludedProductIds || [];
    return (cart.items || []).reduce((sum, item) => (
      excluded.includes(item.product_id) ? sum : sum + item.quantity
    ), 0);
  };

  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  // Scroll lock is refcounted: nested overlays (filter sheet over a drawer)
  // must not unlock the page when only the inner one closes.
  // The class goes on <html> too, not just <body>: the page's horizontal lock
  // (`html, body { overflow-x: clip }` in carousel-drag) blocks body->viewport
  // overflow propagation, so a body-only lock leaves the page scrolling and its
  // scrollbar visible behind the drawer. See base.css `html.scroll-locked`.
  let lockCount = 0;
  const lockScroll = () => {
    if (lockCount++ === 0) {
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = sbw > 0 ? `${sbw}px` : '';
      document.documentElement.classList.add('scroll-locked');
      document.body.classList.add('scroll-locked');
    }
  };
  const unlockScroll = () => {
    if (lockCount > 0 && --lockCount === 0) {
      document.documentElement.classList.remove('scroll-locked');
      document.body.classList.remove('scroll-locked');
      document.body.style.paddingRight = '';
    }
  };

  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };

  /* ------------------------------------------------------------- bindOnce

     THE DUPLICATE-ADD FIX -- Ziad, 2026-09-03.

     connectedCallback runs on EVERY connection, not once. Moving a node
     re-runs it: appendChild/insertBefore on an element that is already in the
     document is a disconnect followed by a reconnect, and this theme moves
     nodes in several places (macaje-affinity.js re-ranks a rail by
     appendChild-ing each existing card back into it; the PDP conversion
     snippets reposition blocks around the buy panel; the theme editor swaps
     sections in place).

     Every element below binds its listeners inside connectedCallback, so a
     moved node came out the other side carrying TWO click handlers -- and one
     tap then ran the handler twice. On <quick-add> and <product-form> that is
     two /cart/add.js requests for one click, which Shopify merges into a
     single line at QUANTITY 2. That is the bug: not the cart, not the
     discount, not the upsell -- one click, two listeners.

     bindOnce() closes it at source. The binding block runs for the first
     connection only, so a node can be moved any number of times and still
     have exactly one of each listener. It is paired with withBusy() below,
     which makes a second concurrent request impossible even if a listener
     ever does get duplicated again. */
  const bindOnce = (el) => {
    if (el.__mcjBound) return false;
    el.__mcjBound = true;
    return true;
  };

  const toast = (msg) => {
    const el = document.getElementById('Toast');
    if (!el) return;
    el.textContent = msg;
    el.dataset.open = 'true';
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.dataset.open = 'false'; }, 3000);
  };

  /* ------------------------------------------------- cart busy / single-flight

     ONE definition of "the cart is working", used by every asynchronous cart
     mutation in the theme: add, quantity change, remove, and the Member/Guest
     switch.

     It does three things, and the third is the important one:

       1. marks the control that started the work with .loading, which the CSS
          turns into a small inline spinner in place of the label (and which
          already carries pointer-events:none from base.css, so the button
          cannot be pressed again);
       2. marks every <cart-drawer> with data-busy, which draws the thin
          progress line under the drawer head -- deliberately NOT a full-panel
          overlay, so the cart never feels slower than it is;
       3. refuses to start a second run under the same key while one is in
          flight, and always clears in a finally block so an error restores the
          normal UI rather than leaving a dead button.

     (3) is a second, independent guarantee against the duplicate-add bug:
     even if a control somehow ends up with two listeners again, the two fire
     in the same tick with the same key and only the first one reaches the
     network. */
  const busyKeys = new Set();

  const setDrawerBusy = (state) => {
    document.querySelectorAll('cart-drawer').forEach((d) => {
      if (state) d.setAttribute('data-busy', '');
      else d.removeAttribute('data-busy');
    });
  };

  const withBusy = async (key, el, fn) => {
    if (busyKeys.has(key)) return undefined;
    busyKeys.add(key);
    if (el) { el.classList.add('loading'); el.setAttribute('aria-busy', 'true'); }
    setDrawerBusy(true);
    try {
      return await fn();
    } finally {
      busyKeys.delete(key);
      if (el) { el.classList.remove('loading'); el.removeAttribute('aria-busy'); }
      if (!busyKeys.size) setDrawerBusy(false);
    }
  };

  /* ------------------------------------------------------------ cart state */

  const Cart = {
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
      return data;
    },
    // opts carries Shopify's `sections`/`sections_url` when the caller wants
    // the re-rendered cart drawer back in the same response as the write.
    add(items, opts) {
      return this.request('/cart/add.js', { items, ...opts });
    },
    change(body) {
      return this.request('/cart/change.js', body);
    },
    update(body) {
      return this.request('/cart/update.js', body);
    },
    async get() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return res.json();
    },
    // One event, one source of truth: every cart surface re-renders from this.
    //
    // `rendered: true` means the caller ALREADY has the freshly rendered
    // cart-drawer section in hand (Shopify returned it alongside the mutation
    // -- see the `sections` parameter used by product-form/quick-add/
    // changeLine) and has applied it. The drawer then skips its own section
    // fetch instead of asking the server for markup it was just given. Every
    // other listener -- ambassador-mode, macaje-affinity -- still receives the
    // event exactly as before; this flag only concerns who renders the drawer.
    publish(cart, opts) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, ...opts } }));
    }
  };

  // busy() is exported alongside the cart API on purpose: snippets/gift-wrap
  // and snippets/quick-add-modal each own a <script> that mutates the cart
  // directly, and they must share this lock rather than keep private ones —
  // otherwise a click in one could overlap a request from the other, and the
  // "cart is working" state would only be true for whichever surface happened
  // to set it.
  Cart.busy = withBusy;
  window.MacajeCart = Cart;

  /* --------------------------------------------------- Luxe Club cart toggle */

  // 2026-08-20 (membership-system spec 5/14/15/29): reverted to a REAL
  // cart-line join. The 2026-08-15 cart-attribute version could not be
  // secured -- cart attributes are writable from any browser console, and an
  // unset attribute read as "Member", so every visitor got 15% off without
  // ever being a member. Joining now adds the actual Application Fee +
  // Membership subscription lines (so checkout creates a real recurring
  // contract), and the Shopify Function authorizes the discount from the
  // app-written luxe.discount_percentage customer metafield or from the
  // Membership line's presence -- never from anything the browser can set.
  //
  // Shared by two toggle instances that can both be on a page at once: the
  // cart-drawer's own copy (which re-renders itself via Cart.publish(), no
  // reload) and the standalone /cart page's copy (main-cart.liquid), which
  // sits outside <cart-drawer> and isn't otherwise reactive to cart changes --
  // that page already relies on full reloads/form-posts for every other cart
  // mutation (see its own comment: "no JS required to update quantities"), so
  // `reload: true` just matches its existing behaviour rather than half-AJAX-
  // enhancing one control on an otherwise server-rendered page.
  async function setLuxeClub(toggle, wantMember, { reload = false, onLoading } = {}) {
    if (!toggle || toggle.classList.contains('loading')) return;
    // One membership switch at a time, whichever surface asked for it. The
    // drawer's copy and the /cart page's copy share this function, and the
    // Member default below calls it too -- without a single key they could
    // race and add the membership line twice.
    if (busyKeys.has('luxe')) return;

    // An existing member has nothing to join -- their state comes from the
    // server-rendered luxe.* customer metafields, and adding a second
    // membership here would create a duplicate subscription (spec 15).
    if (toggle.dataset.luxeState === 'member') return;

    const joining = toggle.dataset.luxeState === 'joining';
    if (wantMember === joining) return;

    const membershipId = Number(toggle.dataset.membershipVariant);
    const membershipProductId = Number(toggle.dataset.membershipProduct);
    const feeId = Number(toggle.dataset.feeVariant);
    const sellingPlanId = Number(toggle.dataset.sellingPlan);
    // feeId is deliberately NOT required: joining no longer adds a fee line, so
    // a missing/unset fee product must not block membership.
    if (!membershipId || !sellingPlanId) {
      toast('Membership is not available right now.');
      return;
    }

    busyKeys.add('luxe');
    setDrawerBusy(true);
    onLoading?.(true);
    try {
      let cart;
      if (wantMember) {
        // MEMBERSHIP ONLY -- the $5 Application Fee line was removed 2026-09-01.
        // The $5 is the first month's membership payment, charged by the selling
        // plan's own first-cycle price, NOT a separate fee. Adding the fee line
        // as well would bill $10 to join and put a line item literally titled
        // "Luxe Club Application Fee" on the checkout page, which is the one
        // surface the theme cannot re-word.
        //
        // feeId is still read above, and the removal branch below still clears
        // it: carts created before this change can contain a fee line, and
        // switching to Guest must still take it out.
        //
        // selling_plan is what makes this a real recurring subscription rather
        // than a one-time purchase.
        const result = await Cart.add(
          [{ id: membershipId, quantity: 1, selling_plan: sellingPlanId }],
          reload ? undefined : { sections: 'cart-drawer', sections_url: window.location.pathname }
        );
        const drawer = toggle.closest('cart-drawer');
        const html = result.sections && result.sections['cart-drawer'];
        const applied = !reload && html ? drawer?.applySection(html) : false;
        // /cart/add.js doesn't include the full cart object. The returned
        // section is already the authoritative visual state, so update the
        // badge and other listeners afterwards instead of holding the choice
        // UI open for a second request.
        if (applied) {
          Cart.get().then((freshCart) => {
            drawer?.updateCount(freshCart);
            Cart.publish(freshCart, { rendered: true });
          }).catch(() => {});
        } else {
          cart = await Cart.get();
        }
      } else {
        const current = await Cart.get();
        const updates = {};
        // Match the membership by PRODUCT, not by variant. This panel is
        // configured with the Monthly variant, but the Legacy invite adds a
        // DIFFERENT variant of the same membership product, and matching on
        // variant meant a Legacy line was never found -- so it could not be
        // removed here. cart-items.liquid hides membership lines from the item
        // list, so this control is the only remover that exists: missing a
        // variant leaves the customer with a subscription and no way out.
        // Falls back to the variant match if the product id is not present.
        current.items.forEach((line) => {
          const isMembership = membershipProductId
            ? line.product_id === membershipProductId
            : line.variant_id === membershipId;
          if (isMembership || line.variant_id === feeId) updates[line.key] = 0;
        });
        if (Object.keys(updates).length) {
          const result = await Cart.update(reload ? { updates } : {
            updates,
            sections: 'cart-drawer',
            sections_url: window.location.pathname,
          });
          const drawer = toggle.closest('cart-drawer');
          const html = result.sections && result.sections['cart-drawer'];
          const applied = !reload && html ? drawer?.applySection(html, result) : false;
          if (applied) Cart.publish(result, { rendered: true });
          else cart = result;
        } else {
          cart = current;
        }
      }

      if (reload) {
        window.location.reload();
        return;
      }
      if (cart) Cart.publish(cart);
    } catch (err) {
      toast(err.message);
    } finally {
      busyKeys.delete('luxe');
      if (!busyKeys.size) setDrawerBusy(false);
      onLoading?.(false);
    }
  }

  /* ------------------------------------------------- GUEST IS THE DEFAULT

     Ziad, 2026-09-13: the cart opens on GUEST. A shopper is never joined to
     the Club on their behalf; they opt in. See the return at the top of
     applyMemberDefault() below -- that is the whole switch.

     Ziad, 2026-09-03 (SUPERSEDED, kept because it still documents the
     machinery below): the cart opens on Member, not Guest.

     In this theme "Member" is not a label -- picking it adds the real Luxe
     Club membership line so checkout can create the subscription (see
     setLuxeClub above). So defaulting to Member means making that choice on
     the shopper's behalf the first time, which is done here, through the same
     setLuxeClub() call and the same 'luxe' lock a manual pick uses. There is
     no second membership code path and no second source of truth: the state
     still comes from the server-rendered data-luxe-state, which is read fresh
     from cart.items on every render.

     Three guards, all necessary:

       * an EXPLICIT pick is remembered (macaje:luxeChoice). Once the shopper
         has chosen Guest, this never runs again for them -- otherwise opting
         out would be undone on the next page load and the membership would be
         impossible to remove.
       * it only runs on a cart that already has real products and whose
         panel is in the 'guest' state. An active member and a cart already
         joining are both left alone.
       * it never runs in Ambassador Mode: that mode hides the whole Club
         panel, so an auto-added membership there would be invisible AND
         unremovable. */
  const LUXE_CHOICE_KEY = 'macaje:luxeChoice';

  const luxeChoice = () => {
    try { return localStorage.getItem(LUXE_CHOICE_KEY); } catch { return null; }
  };
  const rememberLuxeChoice = (value) => {
    try { localStorage.setItem(LUXE_CHOICE_KEY, value); } catch { /* private mode */ }
  };

  // Bounded, not once-only. render() runs after every cart mutation, so with
  // no cap at all a store whose membership product or selling plan is
  // misconfigured would re-try — and re-toast — on every add, remove and
  // quantity change. A hard once-per-page-load cap was too tight in the other
  // direction: a shopper who empties the bag and starts again on the same page
  // would silently stay on Guest, which is not what 'Member is the default'
  // means. A successful attempt does not consume the allowance in practice
  // because the panel comes back in the 'joining' state and the guard below
  // returns before spending another.
  let memberDefaultAttempts = 0;
  const MEMBER_DEFAULT_MAX_ATTEMPTS = 3;

  const applyMemberDefault = () => {
    // GUEST IS THE DEFAULT (Ziad, 2026-09-13). Picking Member in this theme is
    // not a label change -- it adds the real $49.99/mo Luxe Club membership
    // line to the bag. Doing that on a new shopper's behalf meant paid traffic
    // landed on a cart carrying a subscription nobody asked for. The default
    // now has to be the state that charges nobody anything.
    //
    // Everything below is left intact and deliberately unreachable, so this
    // decision is one line to revisit rather than a rebuild. Nothing else
    // changes: an EXPLICIT pick still writes macaje:luxeChoice and still adds
    // or removes the real membership line through setLuxeClub, the panel state
    // still comes from the server-rendered data-luxe-state read fresh from
    // cart.items, and an active member never reaches this panel at all
    // (is_member renders a different branch in luxe-club-toggle.liquid).
    //
    // To restore the Member default, delete this one return.
    return;

    if (memberDefaultAttempts >= MEMBER_DEFAULT_MAX_ATTEMPTS) return;
    if (document.documentElement.getAttribute('data-ambassador-mode') === 'true') return;
    if (luxeChoice()) return;                 // the shopper has already decided
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const toggle = drawer.querySelector('[data-luxe-toggle]');
    if (!toggle || toggle.dataset.luxeState !== 'guest') return;
    // An empty bag has nothing to be a member for; the panel is not rendered
    // in that state anyway, so this is belt and braces.
    if (!drawer.querySelector('[data-line-item]')) return;

    memberDefaultAttempts += 1;
    setLuxeClub(toggle, true, {
      onLoading: (v) => toggle.classList.toggle('loading', v)
    });
  };

  /* -------------------------------------------------------- <sticky-header> */

  // A single IntersectionObserver drives the solid/transparent flip — no scroll
  // handlers, no layout thrash. On the homepage it watches the hero: the header
  // stays transparent while any of the hero shows behind it and goes solid (with
  // its shadow) the moment the hero's bottom passes under the header. Everywhere
  // else it watches a zero-height sentinel at the header's flow position, so the
  // shadow appears after a little scroll. Mega/search open states are layered on
  // top in CSS via .header--mega / .header--search.
  define('sticky-header', class extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.header');
      if (!this.header) return;

      const transparent = this.dataset.transparent === 'true';
      let target = transparent
        ? document.querySelector('[data-hero], .split-hero, .hero, .main > .shopify-section:first-child')
        : null;

      if (!target) {
        const section = this.closest('.shopify-section') || this;
        this.sentinel = document.createElement('div');
        this.sentinel.className = 'header-sentinel';
        this.sentinel.setAttribute('aria-hidden', 'true');
        section.parentNode.insertBefore(this.sentinel, section);
        target = this.sentinel;
      }

      const h = Math.round(this.header.getBoundingClientRect().height) || 66;
      this.io = new IntersectionObserver(
        ([entry]) => this.header.classList.toggle('header--stuck', !entry.isIntersecting),
        { rootMargin: `-${h}px 0px 0px 0px`, threshold: 0 }
      );
      this.io.observe(target);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.sentinel?.remove();
    }
  });

  /* ---------------------------------------------------------- <mega-menu> */

  define('mega-menu', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.trigger = this.querySelector('[data-mega-trigger]');
      this.panel = this.querySelector('[data-mega-panel]');
      if (!this.trigger || !this.panel) return;

      this.close = this.close.bind(this);

      // Hover-intent: a small grace period on leave stops the panel flickering
      // shut when the pointer cuts a corner between trigger and panel.
      on(this, 'mouseenter', () => { clearTimeout(this._t); this.open(); });
      on(this, 'mouseleave', () => { this._t = setTimeout(this.close, 120); });
      on(this.trigger, 'click', (e) => {
        e.preventDefault();
        this.panel.dataset.open === 'true' ? this.close() : this.open();
      });
      on(this, 'keydown', (e) => {
        if (e.key === 'Escape') { this.close(); this.trigger.focus(); }
      });
      // Tabbing out of the last link should dismiss the panel.
      on(this, 'focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.close();
      });
    }

    open() {
      document.querySelectorAll('mega-menu').forEach((m) => { if (m !== this) m.close?.(); });
      this.panel.dataset.open = 'true';
      this.trigger.setAttribute('aria-expanded', 'true');
      // An open panel forces the header solid, even over the transparent hero.
      this.closest('.header')?.classList.add('header--mega');
    }

    close() {
      clearTimeout(this._t);
      this.panel.dataset.open = 'false';
      this.trigger.setAttribute('aria-expanded', 'false');
      // Only release the header state once no panel remains open.
      if (!document.querySelector('mega-menu [data-mega-panel][data-open="true"]')) {
        this.closest('.header')?.classList.remove('header--mega');
      }
    }
  });

  // MACAJE 2.1: an open panel also closes on a click/tap anywhere outside it
  // (touch opens by tap, so there is no mouseleave) and on Escape pressed
  // while focus is elsewhere on the page.
  document.addEventListener('click', (e) => {
    document.querySelectorAll('mega-menu').forEach((m) => {
      if (!m.contains(e.target) && m.panel?.dataset.open === 'true') m.close();
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('mega-menu').forEach((m) => {
      if (m.panel?.dataset.open === 'true') m.close();
    });
  });

  /* ------------------------------------------------------- <drawer-element> */

  class Drawer extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.overlay = this.querySelector('.drawer__overlay');
      this.panel = this.querySelector('.drawer__panel');
      this.close = this.close.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      on(this.overlay, 'click', this.close);
      this.querySelectorAll('[data-drawer-close]').forEach((b) => on(b, 'click', this.close));

      if (this.id) {
        document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((b) => {
          // Toggle, so the same control can close it (the burger becomes an ✕).
          on(b, 'click', (e) => {
            e.preventDefault();
            this.dataset.open === 'true' ? this.close() : this.open(b);
          });
        });
      }
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.dataset.open = 'true';
      this._openers()?.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      lockScroll();
      on(document, 'keydown', this.onKeydown);
      // Wait for the panel to be visible before moving focus into it.
      requestAnimationFrame(() => {
        (this.panel.querySelector('[data-drawer-close]') || this.panel.querySelector(FOCUSABLE))?.focus();
      });
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (this.dataset.open !== 'true') return;
      this.dataset.open = 'false';
      this._openers()?.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      this.opener?.focus();
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    _openers() {
      return this.id ? document.querySelectorAll(`[data-drawer-open="${this.id}"]`) : null;
    }

    onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;

      // Trap focus inside the panel.
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  define('drawer-element', Drawer);

  /* ---------------------------------------------------------- <mobile-nav> */

  // Drill-down mobile navigation: tapping a parent row slides its sub-panel in
  // from the right, "Back" slides it out. One delegated click listener for the
  // whole tree (no per-row binding), and the stack resets whenever the drawer
  // closes so it always reopens at the root.
  define('mobile-nav', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.stack = ['root'];

      on(this, 'click', (e) => {
        const push = e.target.closest('[data-mnav-push]');
        if (push && this.contains(push)) { this.go(push.dataset.mnavPush); return; }
        const pop = e.target.closest('[data-mnav-pop]');
        if (pop && this.contains(pop)) this.back();
      });

      // The drawer sits above this element; reset to root on close. Opening also
      // flips the header to its solid version (gold logo, dark icons) so it reads
      // against the white sheet — the same treatment an open mega panel gets.
      this.drawer = this.closest('drawer-element');
      this.onClose = () => {
        this.reset();
        document.querySelector('.header')?.classList.remove('header--menu');
      };
      this.onOpen = () => {
        this.position();
        document.querySelector('.header')?.classList.add('header--menu');
      };
      if (this.drawer) {
        on(this.drawer, 'drawer:close', this.onClose);
        on(this.drawer, 'drawer:open', this.onOpen);
      }
      this.position();
    }

    disconnectedCallback() {
      if (!this.drawer) return;
      this.drawer.removeEventListener('drawer:close', this.onClose);
      this.drawer.removeEventListener('drawer:open', this.onOpen);
    }

    // The panel starts below the header, whose distance from the top of the
    // viewport depends on whether the announcement bar is still in view. Scroll
    // is locked while open, so measuring once per open is enough.
    position() {
      const header = document.querySelector('.header');
      const bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      this.drawer?.style.setProperty('--drawer-top', `${Math.round(bottom)}px`);
    }

    panel(name) {
      return this.querySelector(`[data-mnav-panel="${CSS.escape(name)}"]`);
    }

    go(name) {
      const next = this.panel(name);
      if (!next) return;
      this.panel(this.stack[this.stack.length - 1])?.classList.remove('is-active');
      this.stack.push(name);
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      next.scrollTop = 0;
      next.querySelector('[data-mnav-pop]')?.focus();
    }

    back() {
      if (this.stack.length < 2) return;
      const leaving = this.panel(this.stack.pop());
      leaving?.classList.remove('is-active');
      leaving?.setAttribute('aria-hidden', 'true');
      const target = this.panel(this.stack[this.stack.length - 1]);
      target?.classList.add('is-active');
      target?.removeAttribute('aria-hidden');
    }

    reset() {
      while (this.stack.length > 1) this.back();
    }
  });

  /* ---------------------------------------------------------- <cart-drawer> */

  define('cart-drawer', class extends Drawer {
    connectedCallback() {
      const first = !this.__mcjBound;
      super.connectedCallback();
      // Everything below binds a listener, so it must not run twice. Without
      // this, a re-connected drawer removed a line twice and switched
      // membership twice per click.
      if (!first) return;
      this._renderSeq = 0;
      this._qtyTimers = new Map();
      // detail.rendered means the publisher already applied the section it
      // was handed by the write (see Cart.publish). Fetching it again here
      // would be a second request for markup we already have on screen --
      // which is precisely the round trip this path exists to remove. Every
      // other publisher (the Luxe toggle, gift-wrap, quick-add-modal) omits
      // the flag and still gets a full render, unchanged.
      on(document, 'cart:updated', (e) => { if (!e.detail.rendered) this.render(e.detail.cart); });
      on(this, 'click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          const removeKey = remove.dataset.cartRemove;
          // Drop any quantity change still sitting in the 200ms coalescing
          // timer for this line. It was read off an input that is about to
          // stop existing, and letting it fire after the removal asks Shopify
          // to change a line that is gone -- which surfaces as an error toast
          // on an action the shopper completed successfully.
          clearTimeout(this._qtyTimers.get(removeKey));
          this._qtyTimers.delete(removeKey);
          // The row is flagged too, so the spinner appears on the line being
          // removed rather than only on the whole drawer.
          this.changeLine(removeKey, 0, remove.closest('[data-line-item]') || remove);
        }
        const luxeOption = e.target.closest('[data-luxe-select]');
        if (luxeOption) {
          e.preventDefault();
          // An explicit pick, so remember it: the Member default must never
          // undo a shopper who chose Guest.
          rememberLuxeChoice(luxeOption.dataset.luxeSelect === 'luxe' ? 'member' : 'guest');
          setLuxeClub(this.querySelector('[data-luxe-toggle]'), luxeOption.dataset.luxeSelect === 'luxe', {
            onLoading: (v) => luxeOption.classList.toggle('loading', v),
          });
        }
      });
      on(this, 'change', (e) => {
        const input = e.target.closest('[data-cart-qty]');
        if (!input) return;
        const key = input.dataset.cartQty;
        const row = input.closest('[data-line-item]') || input;

        // Coalesced per line, and this is a CORRECTNESS fix before it is a
        // performance one. withBusy() keys on the line, so while a change for
        // this line is in flight a second one is DROPPED -- tapping + three
        // times quickly sent "2" and silently discarded "3" and "4", leaving
        // the cart on a quantity the shopper never chose while the input on
        // screen showed the one they did. Coalescing to the final value sends
        // exactly one request, and it is always the number they can see.
        //
        // The typed/tapped number is already visible immediately (it is the
        // input's own value), so this delays only the server sync, never the
        // feedback.
        clearTimeout(this._qtyTimers.get(key));
        this._qtyTimers.set(key, setTimeout(() => {
          this._qtyTimers.delete(key);
          this.changeLine(key, parseInt(input.value, 10), row);
        }, 200));
      });

      this.mergeDuplicateLines();
    }

    // ONE PRODUCT, ONE LINE.
    //
    // Shopify keys a cart line on variant + properties + selling plan, so two
    // adds of the same ring land on two lines the moment anything about their
    // properties differs. This drawer used to attach a hidden `_rec` property
    // to adds from the upsell rail, which is exactly that: adding a piece from
    // "Picked for you" that was already in the bag produced a second line of
    // qty 1 under an unrelated product instead of taking the existing line to 3.
    // That property is gone, so new carts cannot split — but a cart created
    // before the change still carries the old line, and a shopper does not get
    // a fresh cart just because we shipped a fix.
    //
    // This folds any such group back into one line: same variant, same selling
    // plan, no customer-visible properties (a personalised or gift-message line
    // is genuinely distinct and is left alone), quantities summed onto the line
    // that is already first so the bag does not reorder itself. Membership/Fee
    // lines are excluded, the same list the badge and Liquid exclude.
    async mergeDuplicateLines() {
      const cart = await Cart.get().catch(() => null);
      if (!cart?.items?.length) return;

      const excluded = new Set(window.themeStrings?.excludedProductIds || []);
      const groups = new Map();

      cart.items.forEach((item) => {
        if (excluded.has(item.product_id)) return;

        const properties = item.properties || {};
        const customerProperties = Object.keys(properties).filter((key) => !key.startsWith('_'));
        if (customerProperties.length) return;

        const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id || '';
        const groupKey = `${item.variant_id}:${sellingPlanId}`;
        const group = groups.get(groupKey) || [];
        group.push(item);
        groups.set(groupKey, group);
      });

      const updates = {};
      groups.forEach((group) => {
        if (group.length < 2) return;
        const keep = group[0];
        updates[keep.key] = group.reduce((total, item) => total + item.quantity, 0);
        group.forEach((item) => {
          if (item.key !== keep.key) updates[item.key] = 0;
        });
      });

      if (!Object.keys(updates).length) return;

      await withBusy('merge-duplicate-lines', null, async () => {
        const result = await Cart.update({
          updates,
          sections: 'cart-drawer',
          sections_url: window.location.pathname,
        });
        const html = result.sections && result.sections['cart-drawer'];
        const applied = html ? this.applySection(html, result) : false;
        Cart.publish(result, applied ? { rendered: true } : undefined);
      }).catch(() => {});
    }

    // Keyed on the cart line, so a double-tap on + (or a duplicated listener)
    // cannot send the same change twice, while two different lines can still
    // be edited one after the other without either being dropped.
    //
    // Ziad, 2026-09-14 — WHAT "THE CART FEELS UNRELIABLE" WAS.
    //
    // A cart write costs ~525-570ms of Shopify-side discount evaluation before
    // it returns. withBusy() keys on the line and DROPS a second change while
    // the first is in flight, which is right for a double-tap of the same
    // button and wrong for everything else: tap minus and then Remove inside
    // that window and the Remove vanished — no request, no error, no spinner,
    // the item simply stayed in the bag. The shopper taps again, which is how
    // a cart earns a reputation.
    //
    // A second intent for the same line is now QUEUED rather than discarded,
    // and only the newest is kept: whatever the shopper last asked for is what
    // runs when the in-flight write settles. Tapping the same button twice
    // still costs one request, because the second intent is identical to the
    // state the first produced.
    changeLine(key, quantity, el) {
      // Optimistic remove. A cart write costs ~525-570ms of Shopify-side
      // discount evaluation before it returns (measured 2026-09-05), and the
      // row used to sit there, spinner running, until the whole 65KB drawer
      // section had been re-fetched and swapped in -- so a remove read as
      // 0.9s on a good connection and far worse on a bad one.
      //
      // This hides the row on the tap and lets the real request settle behind
      // it. It is not a cosmetic lie: if the change fails the row comes back
      // and the shopper is told why, and the authoritative re-render still
      // replaces the whole list from the server a moment later. Only the
      // ROW is hidden -- no price or total is guessed at, because with eight
      // automatic discounts active on this store any client-side money maths
      // would be wrong more often than right.
      const row = el && el.closest ? el.closest('[data-line-item]') : null;
      const removing = quantity === 0 && !!row;
      if (removing) row.setAttribute('data-removing', '');

      if (!this._lineBusy) { this._lineBusy = new Set(); this._lineQueued = new Map(); }
      if (this._lineBusy.has(key)) {
        this._lineQueued.set(key, quantity);
        return undefined;
      }
      this._lineBusy.add(key);

      const done = () => {
        this._lineBusy.delete(key);
        if (!this._lineQueued.has(key)) return;
        const next = this._lineQueued.get(key);
        this._lineQueued.delete(key);
        // Re-resolve the row from the live DOM: the markup this call was
        // handed has been replaced by the re-render that just landed.
        const liveInput = this.querySelector('[data-cart-qty="' + key + '"]');
        this.changeLine(key, next, liveInput || null);
      };

      return withBusy('line:' + key, el, async () => {
        try {
          // Ask for the re-rendered drawer in the SAME response as the write.
          // /cart/change.js returns the full cart as well, so a remove or a
          // quantity change now costs exactly one round trip instead of two
          // (the write, then a separate section fetch).
          const cart = await Cart.change({
            id: key,
            quantity,
            sections: 'cart-drawer',
            sections_url: window.location.pathname,
          });
          const html = cart.sections && cart.sections['cart-drawer'];
          // applySection returns false if the markup is unusable; in that case
          // fall through to a normal publish so the drawer still re-renders
          // the old way rather than keeping stale contents.
          const applied = html ? this.applySection(html, cart) : false;
          Cart.publish(cart, applied ? { rendered: true } : undefined);
        } catch (err) {
          if (removing) row.removeAttribute('data-removing');
          toast(err.message);
        } finally {
          done();
        }
      });
    }

    // Re-fetch the rendered section rather than templating in JS, so the drawer's
    // markup lives in Liquid and stays editable.
    //
    // Ziad, 2026-08-15: render() is fired-and-forgotten from a `cart:updated`
    // listener (changeLine() doesn't await it), so two mutations in quick
    // succession -- remove an item, then remove another before the first
    // section re-fetch has landed -- kick off two overlapping fetches here.
    // Nothing stopped the SLOWER of the two from resolving last and
    // overwriting the drawer with its (now stale) snapshot, which is what
    // "removed item flickers back" / "drawer feels unreliable" actually was:
    // not the remove call itself failing, but a later click's UI update
    // getting clobbered by an earlier click's late-arriving response. Guarded
    // with a sequence token -- a response only gets applied if it's still the
    // most recent render() call outstanding; anything superseded is dropped.
    // Ziad, 2026-08-15: this used to swap in whatever the section fetch
    // returned with no validation -- if the response wasn't a 200 with a real
    // .drawer__main in it (a dropped request, a transient 5xx, a theme error
    // page), `fresh` came back null and the code silently did nothing,
    // leaving the OLD markup on screen. Since the item-count badge below is
    // driven straight off the `cart` object (not the fetched HTML), the count
    // still updated correctly while the product list underneath stayed
    // exactly as stale as it was before the mutation -- which reads exactly
    // like "the count says 2 but no products are visible." Retries once on a
    // bad response/missing markup before giving up quietly; a real failure
    // still doesn't corrupt anything, it just leaves the previous (still
    // consistent) render in place instead of a half-applied swap.
    /**
     * Swap in a cart-drawer section that has ALREADY been fetched.
     *
     * Split out of render() so the two ways the markup can arrive share one
     * code path: render() goes and gets it, while the mutation paths are
     * handed it by Shopify in the same response as the write itself (the
     * `sections` parameter). Everything after the fetch -- the swap, the
     * rail, the badge, the Member default -- must be identical either way,
     * and now provably is, because it is literally the same lines.
     *
     * Returns false when the markup is unusable, so a caller can fall back to
     * fetching it the old way rather than leaving a half-updated drawer.
     */
    applySection(text, cart) {
      let doc;
      try { doc = new DOMParser().parseFromString(text, 'text/html'); } catch { return false; }
      const fresh = doc.querySelector('.drawer__main');
      if (!fresh) return false;

      // Anything render() has in flight is now stale by definition: this
      // markup came back from the write itself, so it is the newer truth.
      // Bumping the sequence token makes the older response drop itself when
      // it lands, which is the same guard render() already used against
      // out-of-order section fetches.
      this._renderSeq++;

      this.querySelector('.drawer__main').replaceWith(fresh);
      this.removeAttribute('data-pending-add');

      const rail = doc.querySelector('.drawer__rail');
      if (rail && this.querySelector('.drawer__rail')) this.querySelector('.drawer__rail').replaceWith(rail);

      // /cart/add.js returns only the added lines, not a cart, so its caller
      // has no count to give us yet and refreshes the badge separately once
      // the drawer is already open. /cart/change.js does return the cart.
      if (cart) this.updateCount(cart);

      // The panel that was just swapped in is server-rendered from the real
      // cart, so this is the one place that knows the true Member/Guest state
      // after any mutation -- and therefore the right place to apply the
      // Member default. It is a no-op unless the state is 'guest' and the
      // shopper has made no explicit choice.
      applyMemberDefault();
      return true;
    }

    /** The header bag badge, from a real cart object. */
    updateCount(cart) {
      const count = qualifyingCartCount(cart);
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        if (el.textContent !== String(count)) el.textContent = count;
        el.hidden = count === 0;
      });
    }

    async render(cart, attempt = 1) {
      const token = ++this._renderSeq;
      let text;
      try {
        const res = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
        if (!res.ok) throw new Error(`section fetch failed: ${res.status}`);
        text = await res.text();
        if (!/drawer__main/.test(text)) throw new Error('section response missing .drawer__main');
      } catch (err) {
        if (token !== this._renderSeq) return; // superseded meanwhile -- don't bother retrying
        if (attempt < 2) { this.render(cart, attempt + 1); return; }
        console.error('cart-drawer render failed after retry:', err);
        // Give-up path: the contents never arrived, so stop suppressing the
        // empty-bag message. Leaving it set would hide it permanently and the
        // drawer would render as a blank panel on every later open.
        this.removeAttribute('data-pending-add');
        return;
      }
      if (token !== this._renderSeq) return; // a newer render() has since started -- this one's stale
      this.applySection(text, cart);
    }
  });

  /* ----------------------------------------------------------- <quick-add> */

  define('quick-add', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.btn = this.querySelector('button');
      on(this, 'click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Products with real choices go to the PDP; only single-variant
        // products can be added straight from the grid.
        if (this.dataset.url && this.dataset.variantId === '') {
          window.location.href = this.dataset.url;
          return;
        }
        this.add();
      });
    }

    add() {
      // data-variant-ids (comma list) adds a bundle — the set upsell; the
      // single data-variant-id stays the common path.
      const ids = (this.dataset.variantIds || this.dataset.variantId || '')
        .split(',').map((id) => Number(id.trim())).filter(Boolean);
      if (!ids.length) return undefined;

      // Keyed on what is being added, resolved BEFORE any await. Two handlers
      // firing on one click produce the same key in the same tick, so the
      // second is dropped and exactly one /cart/add.js is sent.
      // Open immediately with the same compact pending state used on PDP adds.
      const drawer = document.querySelector('cart-drawer');
      drawer?.setAttribute('data-pending-add', '');
      drawer?.open(this.btn);

      return withBusy('add:' + ids.join(','), this.btn, async () => {
        try {
          const res = await Cart.add(
            ids.map((id) => ({ id, quantity: 1 })),
            { sections: 'cart-drawer', sections_url: window.location.pathname },
          );
          const html = res.sections && res.sections['cart-drawer'];
          // No cart passed: /cart/add.js returns the added lines only, so the
          // badge is refreshed below once the drawer is already on screen.
          const applied = html ? drawer?.applySection(html) : false;
          if (!applied) drawer?.setAttribute('data-pending-add', '');
          toast(window.themeStrings?.addedToCart || 'Added to bag');

          // Off the critical path on purpose: the badge and the non-drawer
          // listeners (ambassador attribution, the affinity rail) do not gate
          // the shopper seeing their item, so they run after the drawer is
          // open rather than in front of it.
          Cart.get()
            .then((cart) => { drawer?.updateCount(cart); Cart.publish(cart, applied ? { rendered: true } : undefined); })
            .catch(() => {});
        } catch (err) {
          drawer?.removeAttribute('data-pending-add');
          toast(err.message);
        }
      });
    }
  });

  /* -------------------------------------------------------- <product-form> */

  define('product-form', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.form = this.querySelector('form');
      this.submit = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      on(this.form, 'submit', (e) => this.onSubmit(e));
    }

    onSubmit(e) {
      // Let Shopify handle it natively if the drawer isn't in use.
      if (this.dataset.cartType !== 'drawer') return undefined;
      e.preventDefault();

      // The key is the variant + quantity actually in the form, read
      // synchronously. A second submit for the same thing while the first is
      // still in flight -- a double tap, the sticky bar's requestSubmit()
      // landing on top of a tap, or a duplicated submit listener -- is
      // dropped rather than charged. snippets/variant-picker.liquid reads
      // .loading on this same button for its own rapid-tap guard, so keeping
      // the class here keeps the two in step.
      const formData = new FormData(this.form);
      const key = 'form:' + (formData.get('id') || '') + ':' + (formData.get('quantity') || '1');

      this.submit.setAttribute('aria-disabled', 'true');
      if (this.errorEl) this.errorEl.hidden = true;

      // Open immediately and use a compact pending state while Shopify writes
      // the cart. The customer sees an acknowledgement on the same tap even
      // when a slow network makes the authoritative section take longer.
      const drawer = document.querySelector('cart-drawer');
      drawer?.setAttribute('data-pending-add', '');
      drawer?.open(this.submit);

      return withBusy(key, this.submit, async () => {
        // aria-disabled is advisory: it does not stop a click or a submit, so a
        // second tap during the ~800ms this takes still reached the handler and
        // was charged as a second unit. withBusy already drops the duplicate
        // REQUEST, but the real invariant is one activation = one add, so the
        // button is made genuinely non-activatable for the life of the add.
        // Set INSIDE the callback, never before withBusy: on a blocked call the
        // callback never runs, so a disabled set outside would never be cleared
        // by the finally below and would brick the button -- reachable, because
        // sticky-atc calls form.requestSubmit(), which fires submit even when
        // the submit button is disabled.
        this.submit.disabled = true;
        try {
          // Ask for the re-rendered drawer alongside the write. FormData is
          // used here (not JSON) because the product form carries properties
          // and selling_plan; Shopify accepts `sections` either way.
          formData.append('sections', 'cart-drawer');
          formData.append('sections_url', window.location.pathname);
          const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { Accept: 'application/json' } });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message);

          const html = data.sections && data.sections['cart-drawer'];
          // No cart argument: /cart/add.js returns the added lines only, so
          // the badge is refreshed below, after the drawer is on screen.
          const applied = html ? drawer?.applySection(html) : false;
          // Fallback only: retain the pending state and let render() fetch the
          // section if Shopify did not include usable drawer markup.
          if (!applied) drawer?.setAttribute('data-pending-add', '');

          // Deliberately not awaited. The badge and the non-drawer listeners
          // (ambassador attribution, affinity rail) must not stand between the
          // shopper and their item.
          Cart.get()
            .then((cart) => { drawer?.updateCount(cart); Cart.publish(cart, applied ? { rendered: true } : undefined); })
            .catch(() => {});
        } catch (err) {
          // The drawer is no longer opened before the write, so there is
          // nothing to close here -- and calling close() would now shut a
          // drawer the shopper had opened themselves, for an error that has
          // nothing to do with it. Just clear the fallback flag and put the
          // message where they are looking: on the form.
          drawer?.removeAttribute('data-pending-add');
          if (this.errorEl) { this.errorEl.textContent = err.message; this.errorEl.hidden = false; }
          else toast(err.message);
        } finally {
          this.submit.disabled = false;
          this.submit.removeAttribute('aria-disabled');
        }
      });
    }
  });

  /* ----------------------------------------------------- <variant-selects> */

  define('variant-selects', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.variants = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '[]');
      on(this, 'change', () => this.onChange());
      on(this, 'click', (e) => {
        const swatch = e.target.closest('[data-option-value]');
        if (!swatch) return;
        const input = document.getElementById(swatch.getAttribute('for') || swatch.dataset.for);
        if (input) { input.checked = true; this.onChange(); }
      });
    }

    get selected() {
      return [...this.querySelectorAll('input:checked')].map((i) => i.value);
    }

    onChange() {
      const variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === this.selected[i])
      );

      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant }
      }));

      if (!variant) return;

      // Keep the URL shareable and the back button honest.
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      this.querySelectorAll('[data-option-label]').forEach((el) => {
        const idx = Number(el.dataset.optionLabel);
        el.textContent = this.selected[idx];
      });
    }
  });

  /* ------------------------------------------------------- <variant-price> */

  define('variant-price', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      on(document, 'variant:change', (e) => this.update(e.detail.variant));
    }

    update(variant) {
      const btn = document.querySelector('[data-add-button]');
      const label = btn?.querySelector('[data-add-label]') || btn;
      const idInput = document.querySelector('[data-variant-id]');
      const savePill = document.querySelector('[data-save-pill]');

      if (!variant) {
        if (btn) { btn.disabled = true; label.textContent = window.themeStrings?.unavailable || 'Unavailable'; }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (btn) {
        btn.disabled = !variant.available;
        label.textContent = variant.available
          ? (window.themeStrings?.addToCart || 'Add To Bag')
          : (window.themeStrings?.soldOut || 'Sold Out');
      }

      const onSale = variant.compare_at_price > variant.price;
      if (savePill) {
        savePill.hidden = !onSale;
        if (onSale) {
          savePill.innerHTML = `${window.themeStrings?.savePrefix || 'Save'} ${moneyHTML(variant.compare_at_price - variant.price)} ${window.themeStrings?.savingsSuffix || ''}`.trim();
        }
      }

      const html = onSale
        ? `<span class="price"><span class="price__now price__now--sale">${moneyHTML(variant.price)}</span><s class="price__was">${moneyHTML(variant.compare_at_price)}</s></span>`
        : `<span class="price"><span class="price__now">${moneyHTML(variant.price)}</span></span>`;

      /* THE MEMBER PRICE MUST SURVIVE A VARIANT CHANGE -- Ziad, 2026-09-03.

         This element wraps everything snippets/price.liquid rendered, which on
         the product page is the regular price AND the Club member line under
         it (snippets/club-price.liquid). Writing this.innerHTML replaced the
         lot, so "Members $X" vanished the instant a shopper picked Silver or
         Gold and never came back.

         Only the regular price is rebuilt now. The member line is left in
         place and re-costed for the new variant by
         snippets/club-price-assets.liquid, which listens to the same
         variant:change event and multiplies by the percentage already resolved
         server-side -- so the two lines always agree and neither is lost. */
      const existing = this.querySelector('.price');
      if (existing) existing.outerHTML = html;
      else this.insertAdjacentHTML('afterbegin', html);
    }
  });

  /* ------------------------------------------------------ <media-gallery> */

  define('media-gallery', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.viewer = this.querySelector('[data-gallery-viewer]');
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.thumbs.forEach((t) => on(t, 'click', () => this.select(t.dataset.mediaId)));
      on(document, 'variant:change', (e) => {
        const id = e.detail.variant?.featured_media?.id;
        if (id) this.select(String(id));
      });
    }

    select(id) {
      const target = this.viewer.querySelector(`[data-media-id="${id}"]`);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.mediaId === id)));
    }
  });

  /* ---------------------------------------------------------- <carousel-el> */

  define('carousel-el', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      // The arrows sit in the section head, OUTSIDE this element — scope the
      // lookup to the section and guard: binding to null crashed the component.
      const scope = this.closest('.shopify-section') || this;
      this.prev = scope.querySelector('[data-carousel-prev]');
      this.next = scope.querySelector('[data-carousel-next]');

      if (this.prev) on(this.prev, 'click', () => this.scrollByDir(-1));
      if (this.next) on(this.next, 'click', () => this.scrollByDir(1));
      on(this.track, 'scroll', debounce(() => this.sync(), 100), { passive: true });
      this.sync();
    }

    scrollByDir(dir) {
      const slide = this.track.firstElementChild;
      if (!slide) return;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      const step = slide.offsetWidth + gap;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const target = Math.max(0, Math.min(max, this.track.scrollLeft + dir * step));

      // Two CSS features on .carousel sabotage programmatic scrolling here:
      //   • scroll-behavior: smooth routes EVERY scrollLeft write through the
      //     browser's smooth engine, which silently no-ops in this environment.
      //   • scroll-snap-type: mandatory yanks intermediate positions back to a
      //     snap point.
      // So we drive the animation ourselves with rAF, and for its duration force
      // scroll-behavior:auto (writes land instantly) and snap:none (no yank),
      // restoring both when done.
      cancelAnimationFrame(this._raf);
      clearTimeout(this._settle);
      const finish = () => {
        cancelAnimationFrame(this._raf);
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = target;
        this.track.style.scrollSnapType = '';
        this.track.style.scrollBehavior = '';
        this.sync();
      };

      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }

      this.track.style.scrollSnapType = 'none';
      this.track.style.scrollBehavior = 'auto';
      const from = this.track.scrollLeft;
      const t0 = performance.now();
      const dur = 340;
      const ease = (k) => 1 - Math.pow(1 - k, 3);
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / dur);
        this.track.scrollLeft = from + (target - from) * ease(k);
        if (k < 1) this._raf = requestAnimationFrame(tick);
        else finish();
      };
      this._raf = requestAnimationFrame(tick);
      // rAF is paused while the tab is backgrounded; a timer still fires and
      // guarantees the scroll lands and the styles restore no matter what.
      this._settle = setTimeout(finish, dur + 120);
    }

    sync() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 1;
    }
  });

  /* ------------------------------------------------------ <accordion-el> */

  define('accordion-el', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.querySelectorAll('[data-accordion-toggle]').forEach((btn) => {
        on(btn, 'click', () => {
          const panel = btn.nextElementSibling;
          const open = btn.getAttribute('aria-expanded') === 'true';

          if (this.dataset.single === 'true' && !open) {
            this.querySelectorAll('[data-accordion-toggle]').forEach((o) => {
              o.setAttribute('aria-expanded', 'false');
              o.nextElementSibling.dataset.open = 'false';
            });
          }

          btn.setAttribute('aria-expanded', String(!open));
          panel.dataset.open = String(!open);
        });
      });
    }
  });

  /* ------------------------------------------------- <facet-form> (filters) */

  define('facet-form', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      on(this.form, 'input', debounce(() => this.apply(), 400));
      on(this.form, 'change', (e) => {
        if (e.target.type !== 'range' && e.target.type !== 'number') this.apply();
      });
      on(window, 'popstate', () => this.apply(false));
    }

    async apply(push = true) {
      const params = new URLSearchParams(new FormData(this.form)).toString();
      const url = `${window.location.pathname}?${params}`;

      document.querySelectorAll('[data-facet-target]').forEach((el) => el.classList.add('loading'));

      try {
        const res = await fetch(url);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');

        document.querySelectorAll('[data-facet-target]').forEach((el) => {
          const id = el.dataset.facetTarget;
          const fresh = html.querySelector(`[data-facet-target="${id}"]`);
          if (fresh) el.innerHTML = fresh.innerHTML;
        });

        if (push) window.history.pushState({}, '', url);
      } catch {
        window.location.href = url;
      } finally {
        document.querySelectorAll('[data-facet-target]').forEach((el) => el.classList.remove('loading'));
      }
    }
  });

  /* ---------------------------------------------------------- <load-more> */

  define('load-more', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.btn = this.querySelector('button');
      this.target = document.querySelector(this.dataset.target);
      if (!this.btn || !this.target) return;

      on(this.btn, 'click', () => this.load());

      if (this.dataset.mode === 'infinite') {
        this.observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) this.load();
        }, { rootMargin: '600px' });
        this.observer.observe(this);
        this.btn.hidden = true;
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      const next = this.dataset.nextUrl;
      if (!next || this.loading) return;
      this.loading = true;
      this.btn.classList.add('loading');

      try {
        const res = await fetch(next);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const items = html.querySelector(this.dataset.target)?.children;
        if (items) this.target.append(...items);

        const fresh = html.querySelector('load-more');
        if (fresh?.dataset.nextUrl) {
          this.dataset.nextUrl = fresh.dataset.nextUrl;
        } else {
          this.observer?.disconnect();
          this.remove();
        }
      } catch {
        window.location.href = next;
      } finally {
        this.loading = false;
        this.btn.classList.remove('loading');
      }
    }
  });

  /* ------------------------------------------------------ <recently-viewed> */

  define('recently-viewed', class extends HTMLElement {
    connectedCallback() {
      const KEY = 'macaje:recently-viewed';
      let ids = [];
      try { ids = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { ids = []; }

      const current = this.dataset.productId;
      if (current) {
        ids = [current, ...ids.filter((i) => i !== current)].slice(0, 12);
        try { localStorage.setItem(KEY, JSON.stringify(ids)); } catch { /* private mode */ }
      }

      const show = ids.filter((i) => i !== current).slice(0, Number(this.dataset.limit || 5));
      if (!show.length) { this.remove(); return; }

      this.fetchProducts(show);
    }

    async fetchProducts(ids) {
      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url = `${window.Shopify.routes.root}search?type=product&q=${encodeURIComponent(query)}&section_id=${this.dataset.sectionId}`;

      try {
        const res = await fetch(url);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const grid = html.querySelector('[data-rv-results]');
        if (grid?.children.length) {
          this.querySelector('[data-rv-target]').replaceChildren(...grid.children);
          this.hidden = false;
        } else {
          this.remove();
        }
      } catch {
        this.remove();
      }
    }
  });

  /* -------------------------------------------------------- <modal-element> */

  // Centred dialog (size guide) on the same open/close/trap mechanics as Drawer.
  define('modal-element', class extends Drawer {});

  /* ------------------------------------------------------- <search-overlay> */

  define('search-overlay', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.trending = this.querySelector('[data-search-trending]');
      this.viewAll = this.querySelector('[data-search-all]');
      this.close = this.close.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      document.querySelectorAll('[data-search-open]').forEach((b) => {
        on(b, 'click', (e) => { e.preventDefault(); this.open(b); });
      });
      this.querySelectorAll('[data-search-close]').forEach((b) => on(b, 'click', this.close));
      on(this.querySelector('.search-overlay__scrim'), 'click', this.close);

      on(this.input, 'input', debounce(() => this.search(), 250));
      this.querySelectorAll('[data-search-chip]').forEach((chip) => {
        on(chip, 'click', () => {
          this.input.value = chip.dataset.searchChip;
          this.input.focus();
          this.search();
        });
      });
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.dataset.open = 'true';
      document.querySelectorAll('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      document.querySelector('.header')?.classList.add('header--search');
      lockScroll();
      on(document, 'keydown', this.onKeydown);
      requestAnimationFrame(() => this.input?.focus());
    }

    close() {
      if (this.dataset.open !== 'true') return;
      this.dataset.open = 'false';
      document.querySelectorAll('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      document.querySelector('.header')?.classList.remove('header--search');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      this.opener?.focus();
    }

    onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
    }

    async search() {
      const q = this.input.value.trim();
      if (this.viewAll) {
        this.viewAll.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(q)}`;
        this.viewAll.hidden = q.length === 0;
      }
      if (this.trending) this.trending.hidden = q.length > 0;
      if (!q) { this.results.innerHTML = ''; return; }

      try {
        const res = await fetch(
          `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`
        );
        const data = await res.json();
        const products = data?.resources?.results?.products || [];
        if (this.input.value.trim() !== q) return; // stale response

        if (!products.length) {
          this.results.innerHTML = `<div class="text-ui text-muted" style="padding: 14px 2px">${window.themeStrings?.noResults || 'No matches found.'}</div>`;
          return;
        }

        this.results.innerHTML = products.map((p) => `
          <a class="search-result" href="${p.url}">
            <span class="search-result__media">${p.featured_image?.url
              ? `<img src="${p.featured_image.url}&width=120" alt="" width="44" height="54" loading="lazy">`
              : ''}</span>
            <span>
              <span class="search-result__title">${p.title}</span>
              <span class="search-result__price" style="display:block">${p.price ? moneyHTML(Math.round(parseFloat(p.price) * 100)) : ''}</span>
            </span>
            <span class="search-result__arrow" aria-hidden="true">→</span>
          </a>`).join('');
      } catch { /* network hiccup — keep whatever is shown */ }
    }
  });

  /* -------------------------------------------------------- <reel-carousel> */

  // Coverflow of 9:16 reels. No cloned slides: each one takes the SHORTEST
  // wrap-around offset from the active index, so the row loops infinitely and
  // any slide that must jump sides does it while fully transparent.
  define('reel-carousel', class extends HTMLElement {
    connectedCallback() {
      // Children can land after upgrade (editor re-render, streaming) — if the
      // slides aren't there yet, watch for them instead of bailing for good.
      if (!this.tryInit()) {
        this.mo = new MutationObserver(() => { if (this.tryInit()) this.mo.disconnect(); });
        this.mo.observe(this, { childList: true, subtree: true });
      }
    }

    tryInit() {
      if (this.inited) return true;
      this.items = [...this.querySelectorAll('.reels__item')];
      if (!this.items.length) return false;
      this.inited = true;
      this.active = 0;
      this.muted = true;

      // The arrows sit in the section head, OUTSIDE this element — scope the
      // lookup to the section and guard: binding to null here crashed the whole
      // component and left every slide stacked at the centre.
      const scope = this.closest('.shopify-section') || document;
      this.prevBtn = scope.querySelector('[data-reel-prev]');
      this.nextBtn = scope.querySelector('[data-reel-next]');
      if (this.prevBtn) on(this.prevBtn, 'click', () => this.go(-1));
      if (this.nextBtn) on(this.nextBtn, 'click', () => this.go(1));

      this.items.forEach((item, k) => {
        on(item.querySelector('.reels__media'), 'click', () => {
          if (k !== this.active) this.set(k);
        });
        const mute = item.querySelector('[data-reel-mute]');
        if (mute) on(mute, 'click', (e) => { e.stopPropagation(); this.toggleMute(); });
      });

      on(this, 'keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
      });

      this.mq = matchMedia('(max-width: 860px)');
      on(this.mq, 'change', () => this.layout());

      // Pause offscreen so autoplaying videos don't burn battery.
      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.syncVideos();
      }, { threshold: 0.15 });
      this.io.observe(this);

      this.layout();
      return true;
    }

    disconnectedCallback() { this.io?.disconnect(); this.mo?.disconnect(); }

    go(dir) { this.set((this.active + dir + this.items.length) % this.items.length); }

    set(k) {
      this.active = k;
      this.layout();
    }

    layout() {
      const n = this.items.length;
      const step = this.mq.matches ? 214 : 236;
      this.items.forEach((item, k) => {
        let o = (((k - this.active) % n) + n) % n;
        if (o > n / 2) o -= n;
        const ao = Math.abs(o);
        const sc = ao === 0 ? 1.12 : ao === 1 ? 0.86 : 0.76;
        const op = ao === 0 ? 1 : ao === 1 ? 0.92 : ao === 2 ? 0.55 : 0;
        const z = ao === 0 ? 30 : ao === 1 ? 20 : ao === 2 ? 10 : 0;
        item.style.setProperty('--tx', `${o * step}px`);
        item.style.setProperty('--sc', sc);
        item.style.setProperty('--op', op);
        item.style.setProperty('--z', z);
        item.dataset.center = String(ao === 0);
        item.setAttribute('aria-hidden', String(ao !== 0));
        // Links/buttons in hidden slides must leave the tab order.
        item.querySelectorAll('a,button').forEach((el) => { el.tabIndex = ao === 0 ? 0 : -1; });
      });
      this.syncVideos();
    }

    syncVideos() {
      this.items.forEach((item, k) => {
        const video = item.querySelector('video');
        if (!video) return;
        video.muted = this.muted;
        const shouldPlay = k === this.active && this.visible !== false &&
          !matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (shouldPlay) video.play().catch(() => {});
        else video.pause();
        item.dataset.playing = String(shouldPlay);
      });
      this.querySelectorAll('[data-reel-mute]').forEach((b) => {
        b.setAttribute('aria-pressed', String(!this.muted));
        b.querySelector('[data-icon-muted]')?.toggleAttribute('hidden', !this.muted);
        b.querySelector('[data-icon-sound]')?.toggleAttribute('hidden', this.muted);
      });
    }

    toggleMute() {
      this.muted = !this.muted;
      this.syncVideos();
    }
  });

  /* ---------------------------------------------------------- <sticky-atc> */

  // Slides in when the buy panel's real CTA leaves the viewport.
  define('sticky-atc', class extends HTMLElement {
    connectedCallback() {
      const sentinel = document.querySelector(this.dataset.watch || '[data-add-button]');
      if (!sentinel) return;

      this.io = new IntersectionObserver((entries) => {
        // Ziad, 2026-08-15: was `String(out || below)` -- on a page where the
        // real Add to Bag button starts below the initial viewport (common on
        // mobile, where the gallery + title + price already fill the screen),
        // the observer's first callback fires not-intersecting with
        // boundingClientRect.top > 0 ("below" -- not yet scrolled to) and that
        // was being treated exactly like "out" (top < 0, scrolled past,
        // above). Both flipped this bar visible immediately on load, before
        // the customer ever reached the real button. Only "out" -- scrolled
        // past and now above the viewport -- should show the sticky bar.
        const out = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
        this.dataset.visible = String(out);
      }, { threshold: 0 });
      this.io.observe(sentinel);

      // Guarded on its own: the IntersectionObserver above is rebuilt on every
      // connection (disconnectedCallback tears it down), but these listeners
      // must be bound once or one tap would submit the form twice.
      if (bindOnce(this)) {
        this.querySelectorAll('[data-sticky-add]').forEach((btn) => {
          on(btn, 'click', () => {
            const form = document.querySelector('product-form form');
            if (form) form.requestSubmit();
          });
        });
      }
    }

    disconnectedCallback() { this.io?.disconnect(); }
  });

  /* ------------------------------------------------------------ <buy-total> */

  // "Add to Bag — $256.00": price × quantity, live.
  define('buy-total', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.price = Number(this.dataset.price || 0);
      this.qtyInput = document.querySelector('[data-buy-qty]');
      on(document, 'variant:change', (e) => {
        if (e.detail.variant) { this.price = e.detail.variant.price; this.render(); }
      });
      if (this.qtyInput) {
        on(this.qtyInput.closest('.qty') || this.qtyInput, 'click', () => setTimeout(() => this.render(), 0));
        on(this.qtyInput, 'change', () => this.render());
      }
      this.render();
    }

    render() {
      const qty = Math.max(1, parseInt(this.qtyInput?.value, 10) || 1);
      this.innerHTML = moneyHTML(this.price * qty);
    }
  });

  /* ------------------------------------------------------------ <qty-input> */

  define('qty-input', class extends HTMLElement {
    connectedCallback() {
      if (!bindOnce(this)) return;
      this.input = this.querySelector('input');
      on(this.querySelector('[data-qty-down]'), 'click', () => this.stepBy(-1));
      on(this.querySelector('[data-qty-up]'), 'click', () => this.stepBy(1));
    }

    stepBy(dir) {
      const v = Math.max(1, (parseInt(this.input.value, 10) || 1) + dir);
      this.input.value = v;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* -------------------------------------------------------------- wishlist */

  // Device-local wishlist: ids in localStorage, hearts + header count in sync.
  const Wishlist = {
    KEY: 'macaje:wishlist',
    get() {
      try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch { return []; }
    },
    set(ids) {
      try { localStorage.setItem(this.KEY, JSON.stringify(ids)); } catch { /* private mode */ }
      this.sync();
    },
    toggle(id) {
      const ids = this.get();
      const next = ids.includes(id) ? ids.filter((i) => i !== id) : ids.concat(id);
      this.set(next);
      return next.includes(id);
    },
    sync() {
      const ids = this.get();
      document.querySelectorAll('[data-wishlist]').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(ids.includes(btn.dataset.wishlist)));
      });
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        // Guard the write so re-syncs don't clobber the macaje-number wrapper
        // span (el.textContent reads back the same digits) and don't retrigger
        // this observer in a loop when nothing changed.
        if (el.textContent !== String(ids.length)) el.textContent = ids.length;
        el.hidden = ids.length === 0;
      });
    }
  };

  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-wishlist]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const onList = Wishlist.toggle(btn.dataset.wishlist);
    toast(onList
      ? (window.themeStrings?.wishlistAdded || 'Saved to your wishlist')
      : (window.themeStrings?.wishlistRemoved || 'Removed from your wishlist'));
  });

  // Header heart without a wishlist page: report the saved count.
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-wishlist-open]');
    if (!btn) return;
    e.preventDefault();
    const n = Wishlist.get().length;
    toast((window.themeStrings?.wishlistCount || 'COUNT saved').replace('COUNT', n));
  });

  /* --------------------------------------------- Luxe Club join links */

  // A link to the membership product carrying ?variant= and ?selling_plan= is
  // a "join" button — the Legacy page's "Accept & activate" is the first one.
  //
  // PROGRESSIVE ENHANCEMENT, on purpose. Cart permalinks (/cart/<id>:1) return
  // 410 for subscription variants, so a plain href cannot add a membership.
  // The href therefore points at the product page with the right variant and
  // plan preselected, which works with no JS at all; this handler skips that
  // hop and takes the buyer straight to checkout.
  //
  // The variant in the URL decides what is bought, which is what keeps Legacy
  // ($50 from month one) apart from the standard plan ($5 first month): the
  // introductory discount is scoped to the standard variant only.
  on(document, 'click', async (e) => {
    const link = e.target.closest('a[href*="/products/luxe-club-membership"]');
    if (!link) return;

    let url;
    try { url = new URL(link.getAttribute('href'), window.location.origin); } catch { return; }
    const id = Number(url.searchParams.get('variant'));
    const sellingPlan = Number(url.searchParams.get('selling_plan'));
    // Not a join link (no variant/plan) — let it navigate normally.
    if (!id || !sellingPlan) return;

    e.preventDefault();
    if (link.dataset.joining) return; // double-click guard
    link.dataset.joining = '1';
    link.setAttribute('aria-busy', 'true');

    try {
      await Cart.add([{ id, quantity: 1, selling_plan: sellingPlan }]);
      window.location.href = '/checkout';
    } catch (err) {
      toast(err.message);
      delete link.dataset.joining;
      link.removeAttribute('aria-busy');
    }
  });

  // Standalone /cart page's own Luxe Club toggle (main-cart.liquid). It renders
  // outside <cart-drawer>, so that element's own delegated listener never sees
  // these clicks -- skip anything that IS inside cart-drawer so a click there
  // isn't handled twice.
  on(document, 'click', (e) => {
    const luxeOption = e.target.closest('[data-luxe-select]');
    if (!luxeOption || luxeOption.closest('cart-drawer')) return;
    e.preventDefault();
    const toggle = luxeOption.closest('[data-luxe-toggle]');
    rememberLuxeChoice(luxeOption.dataset.luxeSelect === 'luxe' ? 'member' : 'guest');
    setLuxeClub(toggle, luxeOption.dataset.luxeSelect === 'luxe', {
      reload: true,
      onLoading: (v) => luxeOption.classList.toggle('loading', v),
    });
  });

  /* ------------------------------------------- Member price -> the Club page

     ONE destination and ONE handler for every surface that shows a member
     price: product cards, collection and search grids, the cart-drawer upsell
     rail, the "Wears well with" rail, Shop the look, and the product page's
     buy panel.

     snippets/club-price.liquid renders a real <a> wherever the HTML allows
     one. In the single place it does not -- a Shop-the-look row is itself an
     <a>, and an anchor may not contain another -- it renders a span carrying
     the same data-club-link. This handler is what makes the two behave
     identically for the shopper, and the stopPropagation is what stops the row
     link underneath from winning the click. A plain anchor elsewhere still
     works with JS off. */
  on(document, 'click', (e) => {
    const hit = e.target.closest('[data-club-link]');
    if (!hit) return;
    // The PDP tooltip button sits inside the same component and is not a link.
    if (e.target.closest('.club-price__info')) return;
    const href = hit.getAttribute('data-club-link');
    if (!href) return;
    e.preventDefault();
    e.stopPropagation();
    window.location.href = href;
  });

  // The span variant carries role="link" tabindex="0", so it has to answer to
  // Enter and Space the way a real link and a button do. The anchors need
  // nothing here — the browser already handles them.
  on(document, 'keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const hit = e.target.closest?.('[data-club-link][role="link"]');
    if (!hit) return;
    e.preventDefault();
    e.stopPropagation();
    window.location.href = hit.getAttribute('data-club-link');
  });

  // Cards arrive via facet re-renders, load-more and carousels — keep hearts
  // truthful whatever injected them.
  const wishlistObserver = new MutationObserver(debounce(() => Wishlist.sync(), 100));

  /* -------------------------------------------------- size-guide scrollbars */

  // Custom, always-visible scroll indicators for the size guide's reference
  // tables (see snippets/size-guide.liquid, snippets/size-guide-scrollbars.liquid).
  // Mobile Safari/Chrome auto-hide the native scroll affordance except mid-
  // gesture — no CSS combination forces it to stay put — so a fixed-height
  // table inside a modal had no visible way to tell it was scrollable. These
  // bars are plain, non-native elements whose size/position/visibility is
  // computed here from the table wrap's real scroll metrics, so they read as
  // permanently on screen on every browser, not just a hover/touch flash.
  const syncTableScrollbars = (root = document) => {
    root.querySelectorAll('.size-guide__table-outer').forEach((outer) => {
      if (outer._scrollbarSync) { outer._scrollbarSync(); return; }

      const area = outer.querySelector('[data-scroll-area]');
      const yTrack = outer.querySelector('[data-scrollbar-y]');
      const xTrack = outer.querySelector('[data-scrollbar-x]');
      if (!area) return;

      const update = () => {
        const overflowY = area.scrollHeight > area.clientHeight + 1;
        const overflowX = area.scrollWidth > area.clientWidth + 1;
        outer.toggleAttribute('data-overflow-y', overflowY);
        outer.toggleAttribute('data-overflow-x', overflowX);

        if (yTrack && overflowY) {
          const thumb = yTrack.firstElementChild;
          const ratio = Math.min(1, area.clientHeight / area.scrollHeight);
          const top = area.scrollTop / (area.scrollHeight - area.clientHeight || 1);
          thumb.style.height = `${Math.max(ratio * 100, 8)}%`;
          thumb.style.top = `${top * (100 - Math.max(ratio * 100, 8))}%`;
        }
        if (xTrack && overflowX) {
          const thumb = xTrack.firstElementChild;
          const ratio = Math.min(1, area.clientWidth / area.scrollWidth);
          const left = area.scrollLeft / (area.scrollWidth - area.clientWidth || 1);
          thumb.style.width = `${Math.max(ratio * 100, 8)}%`;
          thumb.style.left = `${left * (100 - Math.max(ratio * 100, 8))}%`;
        }
      };

      outer._scrollbarSync = update;
      on(area, 'scroll', update, { passive: true });
      update();
    });
  };

  on(window, 'resize', debounce(() => syncTableScrollbars(), 150));
  on(document, 'drawer:open', (e) => syncTableScrollbars(e.target));

  /* -------------------------------------------------------- scroll reveal */

  const reveal = () => {
    const els = document.querySelectorAll('.reveal:not([data-revealed])');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => { el.dataset.revealed = 'true'; });
      return;
    }

    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.dataset.revealed = 'true';
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });

    els.forEach((el) => io.observe(el));
  };

  /* ------------------------------------------------------------ viewport */

  // iOS reports 100vh as the un-collapsed height; this gives drawers a real one.
  const setVh = () => {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  };

  /* ---------------------------------------------------------------- boot */

  setVh();
  reveal();
  Wishlist.sync();
  syncTableScrollbars();
  wishlistObserver.observe(document.body, { childList: true, subtree: true });
  on(window, 'resize', debounce(setVh, 150));

  // The dedicated cart page (sections/main-cart.liquid) is retired -- it bounces
  // straight back here and sets this flag instead of ever rendering, so landing
  // on /cart still ends with the cart open, just as a drawer instead of a page.
  if (sessionStorage.getItem('macaje:openCartDrawer') === '1') {
    sessionStorage.removeItem('macaje:openCartDrawer');
    document.querySelector('cart-drawer')?.open();
  }

  // Member is the default state of the cart, applied once the drawer this page
  // rendered is in the DOM. Re-checked after every cart change from render(),
  // and again the first time the drawer is opened, so a cart that was empty on
  // load still opens on Member once something is in it.
  applyMemberDefault();
  on(document, 'drawer:open', (e) => {
    if (e.target && e.target.id === 'CartDrawer') applyMemberDefault();
  });

  // The Theme Editor swaps section HTML in place; re-run the non-element bits.
  on(document, 'shopify:section:load', () => { reveal(); Wishlist.sync(); syncTableScrollbars(); });

  // Keep every cart count badge in sync no matter which surface changed it.
  on(document, 'cart:updated', (e) => {
    const count = qualifyingCartCount(e.detail.cart);
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (el.textContent !== String(count)) el.textContent = count;
      el.hidden = count === 0;
    });
  });
})();
